import { hasVisibleContent } from "./sanitize";

export const DEFAULT_TEXTAREA_MIN_HEIGHT = 48;
export const DEFAULT_TEXTAREA_MAX_HEIGHT = 160;

const toPositiveNumber = (value, fallback) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
};

// Resize a textarea to fit content while preserving a bounded composer height.
export const resizeTextareaToContent = (textarea, options = {}) => {
  if (!textarea) return;

  const minHeight = toPositiveNumber(options.minHeight, DEFAULT_TEXTAREA_MIN_HEIGHT);
  const maxHeight = Math.max(
    minHeight,
    toPositiveNumber(options.maxHeight, DEFAULT_TEXTAREA_MAX_HEIGHT)
  );

  // Measure from a collapsed state so multiline growth is consistent across browsers.
  textarea.style.height = "0px";
  const measuredHeight = Number(textarea.scrollHeight) || minHeight;
  const nextHeight = Math.min(Math.max(measuredHeight, minHeight), maxHeight);
  textarea.style.height = `${nextHeight}px`;
  textarea.style.overflowY = measuredHeight > maxHeight ? "auto" : "hidden";
};

// Safely get live HTML from the editor ref (supports both getHtml and getHTML variants)
export const getEditorLiveHtml = (editorRef) => {
  try {
    const api = editorRef?.current;
    if (!api) return "";
    if (typeof api.getHtml === 'function') return api.getHtml() || "";
    if (typeof api.getHTML === 'function') return api.getHTML() || "";
    const el = typeof api.getElement === 'function' ? api.getElement() : null;
    return (el && el.innerHTML) || "";
  } catch (_) {
    return "";
  }
};

// Safely get live textContent (normalized) from the editor element
export const getEditorLiveText = (editorRef) => {
  try {
    const api = editorRef?.current;
    const el = typeof api?.getElement === 'function' ? api.getElement() : null;
    const raw = (el && el.textContent) || "";
    // normalize NBSP to spaces and trim
    return raw.replace(/\u00A0/g, ' ').trim();
  } catch (_) {
    return "";
  }
};

// Unified canSend computation used by chat surfaces
export const computeCanSend = ({ editorRef, draftHtml, isSending }) => {
  if (isSending) return false;
  const sanitizedDraftOk = hasVisibleContent(draftHtml || "");
  if (sanitizedDraftOk) return true;
  const liveHtml = getEditorLiveHtml(editorRef);
  if (hasVisibleContent(liveHtml)) return true;
  const liveText = getEditorLiveText(editorRef);
  return !!liveText;
};

// LocalStorage helpers for format bar toggle
export const loadToggle = (key, fallback = true) => {
  try {
    if (typeof window === 'undefined') return fallback;
    const saved = window.localStorage.getItem(key);
    if (saved == null) return fallback;
    return saved === 'true';
  } catch (_) {
    return fallback;
  }
};

export const persistToggle = (key, value) => {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(key, String(!!value));
    }
  } catch (_) {
    // ignore
  }
};

const composerUtils = {
  getEditorLiveHtml,
  getEditorLiveText,
  computeCanSend,
  resizeTextareaToContent,
  loadToggle,
  persistToggle,
};

export default composerUtils;
